import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { CheckCircle, Clock, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import logoImage from '/assets/images/logo-volaptic-1775131861970.png';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const ClienteCheckoutConfermaPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null); // 'loading' | 'succeeded' | 'processing' | 'requires_payment_method' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);

  const paymentIntentClientSecret = searchParams?.get('payment_intent_client_secret');
  const paymentIntentId = searchParams?.get('payment_intent');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      setStatus('loading');

      if (!paymentIntentClientSecret) {
        setStatus('error');
        setErrorMessage('Parametri di pagamento mancanti.');
        return;
      }

      try {
        const stripe = await stripePromise;
        if (!stripe) {
          setStatus('error');
          setErrorMessage('Impossibile caricare il sistema di pagamento.');
          return;
        }

        const { paymentIntent, error } = await stripe?.retrievePaymentIntent(paymentIntentClientSecret);

        if (error) {
          setStatus('error');
          setErrorMessage(error?.message || 'Errore nel recupero dello stato del pagamento.');
          return;
        }

        setStatus(paymentIntent?.status);
      } catch (err) {
        setStatus('error');
        setErrorMessage(err?.message || 'Errore imprevisto. Riprova più tardi.');
      }
    };

    checkPaymentStatus();
  }, [paymentIntentClientSecret]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/">
            <img src={logoImage} alt="Volaptic" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            Pagamento sicuro
          </div>
        </div>
      </header>
      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8">

          {/* Loading */}
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-10 w-10 animate-spin" style={{ color: '#32b3e5' }} />
              <p className="text-sm text-gray-500">Verifica dello stato del pagamento…</p>
            </div>
          )}

          {/* Success */}
          {status === 'succeeded' && (
            <div className="flex flex-col items-center text-center gap-5">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)' }}
              >
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">Pagamento completato!</h1>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Puoi ora contattare il professionista.
                </p>
              </div>
              <Link
                to="/cliente/dashboard"
                className="w-full py-3.5 px-6 rounded-xl font-semibold text-white text-base text-center transition-all flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)' }}
              >
                Vai alla dashboard
              </Link>
            </div>
          )}

          {/* Processing */}
          {status === 'processing' && (
            <div className="flex flex-col items-center text-center gap-5">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">Pagamento in elaborazione</h1>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Ti aggiorneremo via email.
                </p>
              </div>
              <Link
                to="/cliente/dashboard"
                className="w-full py-3.5 px-6 rounded-xl font-semibold text-center text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors rounded-xl"
              >
                Torna alla dashboard
              </Link>
            </div>
          )}

          {/* Failed */}
          {status === 'requires_payment_method' && (
            <div className="flex flex-col items-center text-center gap-5">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">Pagamento non riuscito</h1>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Riprova.
                </p>
              </div>
              <button
                onClick={() => window.history?.back()}
                className="w-full py-3.5 px-6 rounded-xl font-semibold text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                ← Torna indietro
              </button>
            </div>
          )}

          {/* Generic error */}
          {status === 'error' && (
            <div className="flex flex-col items-center text-center gap-5">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">Errore</h1>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {errorMessage || 'Si è verificato un errore imprevisto.'}
                </p>
              </div>
              <button
                onClick={() => window.history?.back()}
                className="w-full py-3.5 px-6 rounded-xl font-semibold text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                ← Torna indietro
              </button>
            </div>
          )}

        </div>
      </main>
      {/* Footer */}
      <footer className="py-6 text-center text-xs text-gray-400">
        © {new Date()?.getFullYear()} Volaptic. Tutti i diritti riservati.
      </footer>
    </div>
  );
};

export default ClienteCheckoutConfermaPage;
